/**
 * Datacrate Model — Firestore storage layer.
 *
 * Single source of truth: Firestore. The architecture also calls for a
 * GCS payload mirror at `gs://deepatomic-dam/{reportType}/v{N}.json`
 * for cheap diff/inspect — that's a later prompt (Phase 1 keeps the
 * minimal viable surface). The Firestore-only path is correct: pointer
 * flip is atomic, versioned docs are immutable, full payload is
 * recoverable from history.
 *
 * Schema:
 *
 *   datacrateModel/{reportType}              → current-pointer doc
 *                                                (the latest DAM payload)
 *   datacrateModel/{reportType}/versions/{N} → immutable historical
 *                                                version (same payload)
 *
 * Write ordering for `saveDam`:
 *   1. Create the versioned doc first. Immutable and create-only: an
 *      existing version doc is never overwritten (firestore.rules also
 *      deny update on `versions/*`). If this fails the pointer is
 *      untouched — the previous good version still serves.
 *   2. Atomic pointer flip — overwrite `datacrateModel/{reportType}`
 *      with the same payload. This is the moment new generations
 *      start picking up the new version.
 *
 * Reads (`loadCurrentDam`, `loadDamVersion`) validate every payload
 * against the Zod schema. Validation failure logs and returns null —
 * the calling pipeline falls through to the inline-build fallback so
 * a corrupt DAM never breaks report generation.
 */
import { collection, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import type { DatacrateModel, ReportType } from '../../types/datacrateModel';
import { safeParseDam } from '../../types/datacrateModel.schema';
import { isNarrativeOnly } from '../../config/reportTypeMeta';
import { damVersionDocId } from './damVersioning';

const DAM_COLLECTION = 'datacrateModel';
const VERSIONS_SUBCOLLECTION = 'versions';

/**
 * SERVER-READ DI (permission-denied fix). damStorage reads via the CLIENT
 * Firestore SDK (`firebase/firestore` + the client `db`), which has NO
 * authenticated user inside a Cloud Run job, so `loadCurrentDam` failed with
 * `permission-denied` there — every report-gen run then ran DEGRADED (no DAM).
 * The report-generation job entry injects an ADMIN-SDK reader via
 * `setDamServerReader`; when present, `loadCurrentDam` uses it and skips the
 * client path. Browser/client callers never set it, so their behaviour is
 * byte-identical. */
let injectedServerReader: ((rt: ReportType) => Promise<DatacrateModel | null>) | null = null;
export function setDamServerReader(reader: (rt: ReportType) => Promise<DatacrateModel | null>): void {
  injectedServerReader = reader;
}

/** Path to the current-pointer doc for a report type. */
function damCurrentRef(reportType: ReportType) {
  return doc(db, DAM_COLLECTION, reportType);
}

/** Path to a specific versioned doc. Version IDs are zero-padded to 4
 *  digits (`v0001`, `v0042`) so lexicographic ordering in Firestore
 *  matches numeric ordering up to 9999 versions per report type. */
function damVersionRef(reportType: ReportType, version: number) {
  return doc(db, DAM_COLLECTION, reportType, VERSIONS_SUBCOLLECTION, damVersionDocId(version));
}

/**
 * Persist a fully-built DAM. Writes the versioned doc first
 * (immutable history), then atomically flips the current-pointer
 * doc. If the version write fails the pointer is untouched.
 *
 * Logs and re-throws on failure so the build script knows the
 * pointer didn't move. The caller decides whether to retry.
 */
export async function saveDam(dam: DatacrateModel): Promise<void> {
  const { reportType, version } = dam;

  // Step 1 — immutable version doc, create-only. If this fails (including
  // because the version already exists), nothing else happens and the
  // existing pointer keeps serving. Reports stamp `damVersionUsed`, so an
  // existing version doc must never be overwritten; firestore.rules deny
  // update on `versions/*` as well, this check just fails early and clearly.
  try {
    const versionRef = damVersionRef(reportType, version);
    const existing = await getDoc(versionRef);
    if (existing.exists()) {
      throw new Error(
        `DAM version doc datacrateModel/${reportType}/versions/${damVersionDocId(version)} already exists — refusing to overwrite an immutable DAM version`,
      );
    }
    await setDoc(versionRef, dam);
  } catch (e) {
    console.error(
      `[damStorage] failed to write version doc datacrateModel/${reportType}/versions/v${version} — pointer unchanged`,
      e,
    );
    throw e;
  }

  // Step 2 — atomic pointer flip. The version doc is already safe;
  // worst case a retry of step 2 produces the same payload (idempotent).
  try {
    await setDoc(damCurrentRef(reportType), dam);
  } catch (e) {
    console.error(
      `[damStorage] failed to flip pointer datacrateModel/${reportType} to v${version} — version doc is safe, please retry`,
      e,
    );
    throw e;
  }
}

/**
 * Load the current DAM for a report type. Reads the pointer doc
 * first; if missing or invalid, falls back to the latest versioned
 * doc (descending order by id). Returns null only if no DAM has
 * ever been written for this type or if every payload is invalid.
 */
export async function loadCurrentDam(reportType: ReportType): Promise<DatacrateModel | null> {
  // W22-2 — DAM-PSR-GUARD. Narrative-only report types (currently just
  // PSR `public-sentiment`) ship WITHOUT a DAM by design — the build
  // pipeline never persists one (see `scripts/buildDam.ts:SUPPORTED_TYPES`)
  // and the narrative is composed directly from the SED + sources via
  // `publicSentimentOrchestrator`. Without this early-return, every
  // call site (`siteRagService.ts`, `sedBootstrap.ts`,
  // `buildDamInline.ts`, `shadowMode.ts`, `SiteLandingPage.tsx`, plus
  // 7 others) fires a pointer read that misses + falls through to a
  // `versions` subcollection ordering query that requires a composite
  // index the project never created (because the query is never
  // legitimately needed). The result was a noisy `[damStorage]
  // loadCurrentDam(public-sentiment) read failed FirebaseError: The
  // query requires an index` console error on every page load that
  // exercised any DAM-aware code path. The shallower commit `c321d6bd`
  // filtered PSR out of `REPORT_TYPES_IN_DAM_DASHBOARD` (1 of 13 call
  // sites); this loader-level guard covers the other 12.
  if (isNarrativeOnly(reportType)) return null;

  // SERVER-READ DI — when an admin reader is injected (Cloud Run job), use it.
  // The client SDK path below can't authenticate there (permission-denied).
  if (injectedServerReader) {
    try {
      return await injectedServerReader(reportType);
    } catch (e) {
      console.warn(`[damStorage] injected server reader failed for ${reportType}`, e);
      return null;
    }
  }

  try {
    const ptrSnap = await getDoc(damCurrentRef(reportType));
    if (ptrSnap.exists()) {
      const parsed = safeParseDam(ptrSnap.data());
      if (parsed.ok) return parsed.value as DatacrateModel;
      console.warn(
        `[damStorage] current-pointer payload for ${reportType} failed schema validation; trying latest version`,
        parsed.errors,
      );
    }

    // Pointer missing or invalid — look up the latest versioned doc.
    // Firestore rejects descending document-key scans ("does not support
    // descending key scans"), so we read the (small) version set and pick the
    // greatest id client-side. Doc ids are zero-padded (`v0001`, `v0042`), so
    // lexicographic order == version order.
    const versionsCol = collection(db, DAM_COLLECTION, reportType, VERSIONS_SUBCOLLECTION);
    const versionsSnap = await getDocs(versionsCol);
    if (versionsSnap.empty) return null;
    const candidate = versionsSnap.docs
      .reduce((a, b) => (a.id >= b.id ? a : b))
      .data();
    const parsed = safeParseDam(candidate);
    if (!parsed.ok) {
      console.warn(
        `[damStorage] latest version doc for ${reportType} also failed schema validation`,
        parsed.errors,
      );
      return null;
    }
    return parsed.value as DatacrateModel;
  } catch (e) {
    console.warn(`[damStorage] loadCurrentDam(${reportType}) read failed`, e);
    return null;
  }
}

/**
 * Enumerate the available versions for a report type, newest first.
 * Used by the admin diff picker (Phase 1 Prompt 7) — does NOT
 * validate every payload (cheap meta-listing).
 *
 * Returns an array of version numbers; an empty array means no DAMs
 * have been written for this type yet.
 */
export async function listDamVersions(reportType: ReportType): Promise<number[]> {
  try {
    const versionsCol = collection(db, DAM_COLLECTION, reportType, VERSIONS_SUBCOLLECTION);
    // No descending __name__ orderBy — Firestore rejects descending key scans.
    // Read the small version set and sort numerically client-side.
    const snap = await getDocs(versionsCol);
    const versions: number[] = [];
    for (const docSnap of snap.docs) {
      // Doc ids are `v0001`, `v0042`, etc.
      const id = docSnap.id;
      if (id.startsWith('v')) {
        const n = parseInt(id.slice(1), 10);
        if (Number.isInteger(n) && n > 0) versions.push(n);
      }
    }
    versions.sort((a, b) => b - a); // newest first
    return versions;
  } catch (e) {
    console.warn(`[damStorage] listDamVersions(${reportType}) read failed`, e);
    return [];
  }
}

/**
 * Load a specific past version. Used by the report viewer's lineage
 * chip to render the exact (reportType, version) tuple a past report
 * was generated against — reproducibility guarantee.
 */
export async function loadDamVersion(
  reportType: ReportType,
  version: number,
): Promise<DatacrateModel | null> {
  if (!Number.isInteger(version) || version < 1) return null;
  try {
    const snap = await getDoc(damVersionRef(reportType, version));
    if (!snap.exists()) return null;
    const parsed = safeParseDam(snap.data());
    if (!parsed.ok) {
      console.warn(
        `[damStorage] version doc v${version} for ${reportType} failed schema validation`,
        parsed.errors,
      );
      return null;
    }
    return parsed.value as DatacrateModel;
  } catch (e) {
    console.warn(`[damStorage] loadDamVersion(${reportType}, ${version}) read failed`, e);
    return null;
  }
}
